import type { BankNames } from '@/constants';
import i18n from '@/i18n';
import { formatPrice } from '@/services/formatter';
import { checkIsValidJSON } from '@/services/helpers/checkIsValidJSON';

import {
  type IBankOffer,
  type ILeasingOffer
} from '../../../AutoCredit/Offers/types/response';
import {
  type IBankResponse,
  type ILeasingResponse,
  type IRequestProduct
} from '../../../AutoCredit/types/api.types';

const t = i18n.t.bind(i18n);

const getAdditionalProducts = (bankOffer: IBankResponse) => {
  const products: string[] = [];

  if (
    bankOffer.insurance_amount &&
    (bankOffer.bank.name === 'bcc' || bankOffer.bank.name === 'bereke')
  ) {
    products.push(
      t('client.additional-services-value', {
        amount: Number(bankOffer.insurance_amount).toLocaleString('ru-RU')
      })
    );
  } else if (
    bankOffer.bank.name === 'altyn' ||
    bankOffer.bank.name === 'halyk'
  ) {
    products.push(t('client.additional-services-gift'));
  }

  if (bankOffer.bank.name === 'bcc') {
    const responseApprovedBody = bankOffer.response_approved_body;
    if (responseApprovedBody && checkIsValidJSON(responseApprovedBody)) {
      const parsed: { isBccPayHolder?: 0 | 1 } =
        JSON.parse(responseApprovedBody);
      if (parsed.isBccPayHolder === 1) {
        products.push(t('client.additional-services-gift-from-bcc-jetour'));
      }
    }
  }

  return products;
};

export const formatBankResponse = (
  responses: IBankResponse[],
  requestProducts: IRequestProduct[]
): IBankOffer[] => {
  const products = requestProducts.filter(
    (item) => item.product.product_type.code !== 2
  );

  return responses
    .filter((item) => item.status !== null)
    .map((response): IBankOffer => ({
      isMainOffer: true,
      offerId: response.uuid,
      extId: response.ext_uuid,
      offeredUuid: response.offered_uuid,
      isOffered: response.is_offered,
      bank: {
        fullname: response.bank.fullname,
        name: response.bank.name as BankNames,
        icon: response.bank.icon
      },
      status: response.status!,
      initialFee: response.initial_payment,
      loanAmount: response.loan_amount,
      rate: response.rate,
      term: response.loan_period?.toString(),
      monthlyPayment: response.monthly_payment,
      insuranceAmount: response.insurance_amount,
      arrears: [],
      bankDescription: response.bank_description,
      responseApprovedBody: response.response_approved_body,
      hasApprovedDate: false,
      licenseNumber: '',
      mainProduct:
        products.find((item) => item.product.bank.uuid === response.bank.uuid)
          ?.product.name || 'Стандарт',
      products: getAdditionalProducts(response),
      alternatives: response.Responses_Alternatives?.map((item) => ({
        uuid: item.uuid,
        description: item.description,
        monthlyPayment: formatPrice(item.monthly_payment),
        initialPayment: formatPrice(item.initial_payment),
        loanAmount: formatPrice(item.loan_amount),
        rate: item.rate,
        offerId: item.response_uuid,
        loanAgreement: item.loan_agreement,
        loanPeriod: item.loan_period,
        responseUuid: item.response_uuid,
        extUuid: item.ext_uuid,
        vehicleCost: item.vehicle_cost,
        paymentDate: item.payment_date,
        insuranceAmount: item.insurance_amount || 0,
        insuranceTerm: item.insurance_term || 0,
        insuranceName: item.insurance_name || ''
      }))
    }));
};

export const formatLeasingResponse = (
  leasingResponse: ILeasingResponse | undefined
): ILeasingOffer[] => {
  if (!leasingResponse) {
    return [];
  }
  const { total_amount, initial_payment } = leasingResponse;

  return leasingResponse.responses.map((response): ILeasingOffer => ({
    approvedAmount: String(response.approved_amount ?? ''),
    balloonAmount: String(response.balloon_amount ?? ''),
    firstMonthPayment: String(response.first_month_payment ?? ''),
    initialPayment: String(response.initial_payment ?? initial_payment ?? ''),
    insuranceAmount: response.insurance_amount ?? 0,
    telematicsAmount: response.telematics_amount ?? 0,
    leasingCompany: response.Leasing_Company,
    loanPeriod: String(response.loan_period ?? ''),
    rate: String(response.rate ?? ''),
    status: response.status,
    totalAmount: String(total_amount ?? ''),
    uuid: response.uuid,
    leasingExtId: leasingResponse.external_reference_id,
    externalHandoffToken: leasingResponse.external_handoff_token
  }));
};
